<template>
  <div class="app-container">
    <div class="title-row"><span class="title">{{ this.$route.meta.title }}</span></div>
    <el-form :model="queryParams" ref="queryForm" size="small" :inline="true" v-show="showSearch" label-width="68px">
      <el-form-item label="所属省份" prop="provinceId" @change="handleQuery">
        <el-select v-model="queryParams.provinceId">
          <el-option v-for="item in provinceOptions" :key="item.provinceId" :label="item.provinceName" :value="item.provinceId"/>
        </el-select>
      </el-form-item>
      <el-form-item label="地区名称" prop="areaName">
        <el-input v-model="queryParams.areaName" placeholder="请输入地区名称" clearable @keyup.enter.native="handleQuery"/>
      </el-form-item>
      <el-form-item label="地区状态" prop="status" >
        <el-select v-model="queryParams.status" placeholder="地区状态" @change="handleQuery">
          <el-option v-for="dict in dict.type.forest_area_status" :key="dict.value" :label="dict.label" :value="dict.value"/>
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" size="mini" @click="handleQuery">搜索</el-button>
        <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">重置</el-button>
      </el-form-item>
    </el-form>

    <el-row :gutter="10" class="mb8">
      <el-col :span="1.5">
        <el-button v-hasPermi="['forest:area:add']" type="primary" plain icon="el-icon-plus" size="mini" @click="handleAdd">新增</el-button>
      </el-col>
      <el-col :span="1.5">
        <el-button v-hasPermi="['forest:area:export']" type="warning" plain icon="el-icon-download" size="mini" @click="handleExport">导出</el-button>
      </el-col>
      <el-col :span="1.5">
        <el-button type="warning" plain icon="el-icon-close" size="mini" @click="handleClose">关闭</el-button>
      </el-col>
      <right-toolbar :showSearch.sync="showSearch" @queryTable="getList" ></right-toolbar>
    </el-row>

    <el-table v-loading="loading" :data="dataList"  border>
      <el-table-column type="index" :index="handleIndex" label="序号" align="center" width="60"/>
      <el-table-column label="地区名称" align="center" prop="areaName" />
      <el-table-column label="状态" align="center" prop="status">
        <template slot-scope="scope">
          <dict-tag :options="dict.type.forest_area_status" :value="scope.row.status"/>
        </template>
      </el-table-column>
      <el-table-column label="操作" align="center" class-name="small-padding fixed-width" width="200">
        <template slot-scope="scope">
          <el-button v-hasPermi="['forest:area:edit']" size="mini" type="text" icon="el-icon-edit" @click="handleUpdate(scope.row)" >修改</el-button>
          <el-button v-hasPermi="['forest:area:remove']" size="mini" type="text" icon="el-icon-delete" @click="handleDelete(scope.row)" >删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <pagination
        v-show="total>0"
        :total="total"
        :page.sync="queryParams.current"
        :limit.sync="queryParams.size"
        @pagination="getList"
        :page-sizes="[10, 20]"
    />

    <!-- 添加或修改参数配置对话框 -->
    <el-dialog :title="title" :visible.sync="open" width="500px" append-to-body :close-on-click-modal="false" >
      <el-form ref="form" :model="form" :rules="rules" label-width="80px">
        <el-form-item label="所在省份" prop="provinceId">
          <el-select v-model="form.provinceId" disabled>
            <el-option v-for="item in provinceOptions" :key="item.provinceId" :label="item.provinceName" :value="item.provinceId"/>
          </el-select>
        </el-form-item>
        <el-form-item label="地区名称" prop="areaName">
          <el-input v-model="form.areaName" placeholder="请输入地区名称" />
        </el-form-item>
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="form.status">
            <el-radio
              v-for="dict in dict.type.forest_area_status"
              :key="dict.value"
              :label="dict.value"
            >{{dict.label}}</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button type="primary" @click="submitForm">确 定</el-button>
        <el-button @click="cancel">取 消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listForestArea, getForestArea, delForestArea, addForestArea, updateForestArea } from "@/api/forest/area";
import { optionSelect as getProvinceOptionSelect } from "@/api/forest/province";

export default {
  name: "ForestArea",
  dicts: ['forest_area_status'],
  data() {
    return {
      // 遮罩层
      loading: true,
      // 显示搜索条件
      showSearch: true,
      // 总条数
      total: 0,
      // 地区表格数据
      dataList: [],
      // 默认地区所属省份
      defaultProvinceId: "",
      // 弹出层标题
      title: "",
      // 是否显示弹出层
      open: false,
      // 类型数据地区
      provinceOptions: [],
      // 查询参数
      queryParams: {
        current: 1,
        size: 10,
        provinceId: undefined,
        areaName: undefined,
        status: undefined
      },
      // 表单参数
      form: {},
      // 表单校验
      rules: {
        areaName: [
          { required: true, message: "地区名称不能为空", trigger: "blur" }
        ],
        status: [
          { required: true, message: "地区状态不能为空", trigger: "blur" }
        ]
      }
    };
  },
  created() {
    const provinceId = this.$route.query && this.$route.query.provinceId
    this.defaultProvinceId = provinceId
    this.getProvinceList(provinceId);
  },
  methods: {
    handleIndex(index){
      return (this.queryParams.current - 1) * this.queryParams.size + (index + 1)
    },
    /** 查询地区类型列表 */
    getProvinceList(provinceId) {
      getProvinceOptionSelect().then(response => {
        this.provinceOptions = response.data;
        this.queryParams.provinceId = Number.parseInt(provinceId);
        this.getList();
      });
    },
    /** 查询地区数据列表 */
    getList() {
      this.loading = true;
      listForestArea(this.queryParams).then(res => {
        this.dataList = res.rows;
        this.total = res.total;
        this.loading = false;
      });
    },
    // 取消按钮
    cancel() {
      this.open = false;
      this.reset();
    },
    // 表单重置
    reset() {
      this.form = {
        provinceId: this.queryParams.provinceId,
        areaName: undefined,
        status: "0",
      };
      this.resetForm("form");
    },
    /** 搜索按钮操作 */
    handleQuery() {
      this.queryParams.current = 1;
      this.getList();
    },
    /** 返回按钮操作 */
    handleClose() {
      const obj = { path: '/forest/forest-province' };
      this.$tab.closeOpenPage(obj)
    },
    /** 重置按钮操作 */
    resetQuery() {
      this.resetForm("queryForm");
      this.queryParams.provinceId = this.defaultProvinceId;
      this.handleQuery();
    },
    /** 新增按钮操作 */
    handleAdd() {
      this.reset();
      this.open = true;
      this.title = "添加地区数据";
      //this.form.provinceId = this.queryParams.provinceId;
      //this.$set(this.form,"provinceId", this.queryParams.provinceId)
    },

    /** 修改按钮操作 */
    handleUpdate(row) {
      this.reset();
      getForestArea(row.areaId).then(response => {
        this.form = response.data;
        this.open = true;
        this.title = "修改地区数据";
      });
    },
    /** 提交按钮 */
    submitForm() {
      this.$refs["form"].validate(valid => {
        if (valid) {
          if (this.form.areaId != undefined) {
            updateForestArea(this.form).then(response => {
              this.$modal.msgSuccess("修改成功");
              this.open = false;
              this.getList();
            });
          } else {
            addForestArea(this.form).then(response => {
              this.$modal.msgSuccess("新增成功");
              this.open = false;
              this.getList();
            });
          }
        }
      });
    },
    /** 删除按钮操作 */
    handleDelete(row) {
      this.$modal.confirm('是否确认删除地区名称为"' + row.areaName + '"的数据项？').then(function() {
        return delForestArea(row.areaId);
      }).then(() => {
        this.getList();
        this.$modal.msgSuccess("删除成功");
      }).catch(() => {});
    },
    /** 导出按钮操作 */
    handleExport() {
      this.download('admin/forest/area/export', {
        ...this.queryParams
      }, `蚂蚁森林地图地区_${new Date().getTime()}.xlsx`)
    }
  }
};
</script>
